/**
 * React Query hooks for Claro plans + subscriptions (CLA-847).
 *
 * `useSubscription` is the clinician-facing read (active clinic's plan,
 * entitlements, note usage) that powers the Settings plan card. The admin
 * hooks drive Manage > Platform > Subscriptions (per-clinic plan assignment,
 * behind `admin:*`).
 *
 * Backend: lib/claro_web/controllers/api/v1/subscription_controller.ex and
 * subscription_admin_controller.ex
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import type { components } from './schema'
import { regionalText } from '@/lib/regional-copy'

/**
 * CLA-980 added `store_unlocked`: true only when the clinic has been
 * deliberately unlocked for store purchasing (distinct from the "store"
 * plan feature, which standard plans carry). Declared as an augmentation
 * for the same schema-drift reason as `Plan` below.
 */
export type Subscription = components['schemas']['SubscriptionResponse'] & {
  store_unlocked: boolean
  /**
   * CLA-1167: whether this clinic's writes will be refused right now. Folds
   * both halves of the lock server-side — the plan's `limits.writes_locked`
   * and the enforcement flag — so nothing here re-derives a policy that
   * lives in two settings rows. Same schema-drift augmentation as above.
   */
  writes_locked: boolean
  /**
   * CLA-1573: whether THIS user can see and request the clinic's Claro
   * invoices — a clinic manager, at a clinic that has been billed. Optional
   * so a web build that reaches a server without it just hides the list.
   */
  can_view_invoices?: boolean
  /**
   * CLA-1574: present only while a subscription payment has failed — when
   * the daily sweep will pause the plan if it stays unpaid. Web writes its
   * own words from this (it may link to the fix); the apps render the
   * server's `past_due_notice` instead.
   */
  past_due_pause_at?: string | null
}

/** One of the clinic's Claro subscription invoices (CLA-1573). */
export interface SubscriptionInvoice {
  id: string
  number: string | null
  /** Stripe's invoice status — paid, open, void, uncollectible. Open string. */
  status: string
  total_cents: number
  amount_paid_cents: number
  currency: string
  created_at: string | null
  paid_at: string | null
  /** Stripe-hosted page; for an open invoice, where it is paid. */
  hosted_invoice_url: string | null
  pdf_url: string | null
}

/**
 * CLA-937 added `monthly_base_price` / `annual_base_price` to the Plan schema:
 * a clinic-level base that INCLUDES the first practitioner, with the seat price
 * applying to each ADDITIONAL practitioner. Null = seat-only plan, where the
 * seat price applies to everyone.
 *
 * Declared here rather than picked up from `schema.d.ts` because that file has
 * drifted thousands of lines behind the backend spec, and regenerating it is a
 * change that deserves its own typecheck rather than riding along with a copy
 * tweak. Delete this augmentation the moment `codegen/generate.sh typescript`
 * is run — the fields are already in `priv/openapi/v1.json`.
 */
export type Plan = components['schemas']['Plan'] & {
  monthly_base_price: string | null
  annual_base_price: string | null
}
export type AdminSubscriptionRow = components['schemas']['AdminSubscriptionRow']

export interface AssignPlanInput {
  clinicId: number
  plan: string
  status?: string
  trial_ends_at?: string
}

const SUBSCRIPTION_KEY = ['subscription']
const PLANS_KEY = ['subscription', 'plans']
const ADMIN_SUBSCRIPTIONS_KEY = ['admin', 'subscriptions']

export function useSubscription() {
  return useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: () => api.get<Subscription>('/subscription'),
  })
}

/**
 * Public plan catalogue (names, per-seat prices, features) — CLA-850.
 *
 * `enabled` exists because this is a billing-surface catalogue that most
 * callers only need conditionally (CLA-1170). `TrialStrip` mounts on every
 * page but renders nothing unless the subscription is actually `trialing`, so
 * fetching unconditionally spent one authenticated read per cold boot, for
 * every non-trialing user, on a response that was then discarded. Defaults to
 * on, so callers that genuinely always need it are unaffected.
 */
export function usePlans({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: PLANS_KEY,
    queryFn: () => api.get<{ plans: Plan[] }>('/subscription/plans'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

/**
 * Start a Stripe Checkout session for the active clinic (CLA-850). On
 * success the caller redirects to the returned hosted URL.
 */
export function useCheckout() {
  return useMutation({
    mutationFn: (input: { plan: string; interval: 'monthly' | 'annual' }) =>
      api.post<{ url: string }>('/subscription/checkout', input),
  })
}

/** Open the Stripe Billing Portal (payment method, invoices, cancel). */
export function useBillingPortal() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>('/subscription/portal'),
  })
}

/** The active clinic's Claro invoices (CLA-1573), managers only. */
export function useSubscriptionInvoices({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...SUBSCRIPTION_KEY, 'invoices'],
    queryFn: () => api.get<{ invoices: SubscriptionInvoice[] }>('/subscription/invoices'),
    enabled,
  })
}

/** Email the paid invoices to the signed-in user's own address (CLA-1573). */
export function useEmailReceipts() {
  return useMutation({
    mutationFn: () =>
      api.post<{ sent_to: string; count: number }>('/subscription/invoices/email'),
  })
}

export function useAdminSubscriptions() {
  return useQuery({
    queryKey: ADMIN_SUBSCRIPTIONS_KEY,
    queryFn: () =>
      api.get<{ subscriptions: AdminSubscriptionRow[]; plans: Plan[] }>('/admin/subscriptions'),
  })
}

export function useAssignPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ clinicId, ...body }: AssignPlanInput) =>
      api.put<Subscription>(`/admin/subscriptions/${clinicId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_SUBSCRIPTIONS_KEY })
      // The assigned clinic may be the viewer's own active clinic.
      qc.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
    },
  })
}

/**
 * Extract the human-readable message from a 402 note_cap_reached error
 * (the free tier's monthly AI note limit, CLA-847). Returns null for any
 * other error so callers fall through to their generic handling.
 */
export interface PlanRequiredInfo {
  feature: string
  required_plan: string | null
  detail: string
}

/**
 * Decode a 402 plan_required error (CLA-906 feature gates). Returns null
 * for any other error so callers fall through to generic handling. The
 * same shape rides the global `claro:plan-required` event from the api
 * client, so most surfaces never need to call this directly.
 */
export function planRequired(err: unknown): PlanRequiredInfo | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null
  const body = err.body as
    | { code?: string; detail?: string; feature?: string; required_plan?: string | null }
    | undefined
  if (body?.code !== 'plan_required' || !body.feature) return null
  return {
    feature: body.feature,
    required_plan: body.required_plan ?? null,
    detail: body.detail || regionalText("This feature isn't included in this clinic's current plan."),
  }
}

export function noteCapMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null
  const body = err.body as { code?: string; detail?: string } | undefined
  if (body?.code !== 'note_cap_reached') return null
  return body.detail || 'Monthly AI note limit reached — upgrade your plan to keep generating notes.'
}
